import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cx from "clsx";
import React from "react";
import Button from "@ingka/button";
import SSRIcon from "@ingka/ssr-icon";
import Text from "@ingka/text";
import { useBoolean } from "../hooks/useBoolean";
import { PopOver } from "./PopOver";
let handlerStack = [];
const escapeListener = (evt) => {
    const { key, defaultPrevented } = evt;
    if (defaultPrevented) {
        return;
    }
    if (key === "Escape" || key === "Esc") {
        handlerStack[0]?.(evt);
    }
};
export const useEscapeListenerStack = (handler) => {
    React.useEffect(() => {
        handlerStack = [handler, ...handlerStack];
        if (handlerStack.length === 1) {
            window.addEventListener("keydown", escapeListener);
        }
        return () => {
            handlerStack = handlerStack.filter((h) => h !== handler);
            if (handlerStack.length === 0) {
                window.removeEventListener("keydown", escapeListener);
            }
        };
    }, [handler]);
};
export const useVisibleFocus = () => {
    const [isPointer, setPointerOn, setPointerOff] = useBoolean(false);
    const [visibleFocus, setVisibleFocus] = React.useState(true);
    const determineVisibleFocus = React.useCallback(() => {
        if (isPointer) {
            return setVisibleFocus(false);
        }
        return setVisibleFocus(true);
    }, [isPointer, setVisibleFocus]);
    return {
        setPointerOn,
        setPointerOff,
        determineVisibleFocus,
        visibleFocus,
    };
};
const remainder = (dividend, divisor) => ((dividend % divisor) + divisor) % divisor;
export const MenuButton = React.forwardRef(({ disabled, name, onClick, className, onPointerDown, onPointerUp, onFocus, children, icon, ...props }, ref) => {
    const { setPointerOn, setPointerOff, determineVisibleFocus } = useVisibleFocus();
    const handleFocus = React.useCallback((e) => {
        onFocus?.(e);
        determineVisibleFocus();
    }, [determineVisibleFocus, onFocus]);
    const handlePointerDown = React.useCallback((e) => {
        onPointerDown?.(e);
        setPointerOn();
    }, [setPointerOn, onPointerDown]);
    const handlePointerUp = React.useCallback((e) => {
        onPointerUp?.(e);
        setPointerOff();
    }, [setPointerOff, onPointerUp]);
    return (_jsx(Button, { iconOnly: true, size: "small", type: "tertiary", ref: ref, ssrIcon: icon, disabled: disabled, name: name, onClick: onClick, onPointerDown: handlePointerDown, onPointerUp: handlePointerUp, onFocus: handleFocus, ...props, children: children }));
});
MenuButton.displayName = "MenuButton";
var MenuKeys;
(function (MenuKeys) {
    MenuKeys["Space"] = " ";
    MenuKeys["Enter"] = "Enter";
    MenuKeys["Escape"] = "Escape";
    MenuKeys["Esc"] = "Esc";
    MenuKeys["Home"] = "Home";
    MenuKeys["End"] = "End";
    MenuKeys["ArrowRight"] = "ArrowRight";
    MenuKeys["ArrowUp"] = "ArrowUp";
    MenuKeys["ArrowLeft"] = "ArrowLeft";
    MenuKeys["ArrowDown"] = "ArrowDown";
})(MenuKeys || (MenuKeys = {}));
export const MenuList = ({ onEscape, children, className, ...props }) => {
    useEscapeListenerStack(onEscape);
    return (_jsx("div", { className: cx("pdpp-react-menuContainer", className), ...props, children: children }));
};
const BaseItem = ({ component, onClick, disabled = false, keyboardSelect = false, }) => {
    const clickHandler = React.useCallback((event) => {
        if (disabled === true) {
            event.stopPropagation();
            return;
        }
        onClick(event);
    }, [disabled, onClick]);
    const preventMenuBlur = React.useCallback((e) => {
        e.preventDefault();
    }, []);
    return (_jsx("div", { className: cx("pdpp-react-menuBaseItem", {
            "pdpp-react-menuItemDisabled": disabled,
            "pdpp-react-menuItemKeyboardSelect": keyboardSelect,
        }), onPointerDown: preventMenuBlur, onClick: clickHandler, children: component }));
};
export const BaseMenu = React.memo(({ align = "left", disabled = false, components, onBlur, icon: menuIcon, ...props }) => {
    const [menuVisible, openMenu, hideMenu] = useBoolean(false);
    const anchorRef = React.useRef(null);
    const [arrowIndex, setArrowIndex] = React.useState(-1);
    const hideAndBlurMenu = React.useCallback(() => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        hideMenu();
    }, [hideMenu]);
    const handleBlur = React.useCallback((e) => {
        onBlur?.(e);
        hideMenu();
    }, [onBlur, hideMenu]);
    const mouseToggleMenu = React.useCallback((event) => {
        event.stopPropagation();
        if (menuVisible) {
            hideAndBlurMenu();
        }
        else {
            setArrowIndex(-1);
            openMenu();
        }
    }, [menuVisible, hideAndBlurMenu, openMenu]);
    const moveMainArrowMenuIndex = React.useCallback((increment) => {
        const nextIndex = remainder(arrowIndex + increment, components.length);
        return setArrowIndex(nextIndex);
    }, [arrowIndex, components.length]);
    const handleKeyDown = React.useCallback((event) => {
        if (!(event.key in MenuKeys)) {
            return;
        }
        event.preventDefault();
        switch (event.key) {
            case MenuKeys.Enter:
            case MenuKeys.Space: {
                if (menuVisible) {
                    if (components[arrowIndex]?.disabled !== true) {
                        components[arrowIndex]?.onClick(event);
                        hideMenu();
                        break;
                    }
                }
                openMenu();
                setArrowIndex(0);
                break;
            }
            case MenuKeys.Escape:
            case MenuKeys.Esc: {
                hideMenu();
                break;
            }
            case MenuKeys.ArrowUp: {
                if (menuVisible) {
                    moveMainArrowMenuIndex(-1);
                    break;
                }
                openMenu();
                setArrowIndex(0);
                break;
            }
            case MenuKeys.ArrowDown: {
                if (menuVisible) {
                    moveMainArrowMenuIndex(1);
                    break;
                }
                openMenu();
                setArrowIndex(0);
                break;
            }
            case MenuKeys.ArrowRight:
            case MenuKeys.ArrowLeft: {
                hideMenu();
                break;
            }
            case MenuKeys.Home: {
                if (menuVisible) {
                    setArrowIndex(0);
                    break;
                }
                break;
            }
            case MenuKeys.End: {
                if (menuVisible) {
                    setArrowIndex(components.length - 1);
                    break;
                }
                break;
            }
            default:
        }
    }, [
        menuVisible,
        openMenu,
        components,
        arrowIndex,
        hideMenu,
        moveMainArrowMenuIndex,
    ]);
    return (_jsxs("div", { className: "pdpp-react-menuAnchor", ref: anchorRef, onKeyDown: handleKeyDown, onBlur: handleBlur, ...props, children: [_jsx(MenuButton, { icon: menuIcon, onClick: mouseToggleMenu, disabled: disabled }), menuVisible ? (_jsx(PopOver, { horizontalPosition: align, horizontalAlignment: align, anchorEl: anchorRef.current, onScroll: hideAndBlurMenu, children: _jsx(MenuList, { onEscape: hideMenu, onClick: hideAndBlurMenu, children: components.map((component, index) => (_jsx(BaseItem, { keyboardSelect: index === arrowIndex, ...component }, index))) }) })) : null] }));
});
BaseMenu.displayName = "BaseMenu";
export const Menu = React.memo(({ items, ...props }) => {
    const components = React.useMemo(() => items.map((item) => {
        const { icon, label } = item;
        const menuItemContent = icon !== undefined ? (_jsx(SSRIcon, { className: "pdpp-react-menuIconStyles", paths: icon })) : undefined;
        return {
            component: (_jsxs("div", { className: "pdpp-react-menuItem", "data-testid": "menuItem", children: [menuItemContent, _jsx(Text, { tagName: "span", textStyle: "Body.S", children: label })] })),
            ...item,
        };
    }), [items]);
    return _jsx(BaseMenu, { components: components, ...props });
});
Menu.displayName = "Menu";
//# sourceMappingURL=Menu.js.map